import moment, { months } from "moment";
import { newCommonFormatCurrency } from "../services/mathServices";

/**
 * TODO. There is a copy of this type in the frontendm in the types folder. It should be moved to a shared location.
 */
export enum PaymentPeriodicity {
    MONTHLY = 1,
    BIMONTHLY = 2,
    TRIMONTHLY = 3,
    SEMIANNUAL = 6,
    ANNUAL = 12,
}

export type LoanDetailsDTO = {
    title: string;
    periodicity?: PaymentPeriodicity;
    startDate: string;
    signatureDate: string;
    type: string;
    invoiceNumber: string;
    status: "ANALYSED" | "VALIDATED";
    bobbeeStartDate: string;
    amortisationType: string;
    loanPeriod: number;
    netAmount: number;
    grossAmount: number;
    loanRate: number;
    insuranceType: "PERCENTAGE" | "AMOUNT";
    insuranceRate: number;
    insuranceAmount: number;
    monthlyLoanRate: number;
    aaMonthlyName: string;
    aaInterestName: string;
    aaInsuranceName: string;
    aaBankName: string;
    aaIraName: string;
    iraType: {
        value: string;
        text: string;
    };
    redemptionType: string;
    iraRedemptionAmount: number;
    loanEarlyRedemption: boolean;
    earlyRedemptionAmount: number;
    earlyRedemptionInterest: number;
    earlyRedemptionInsurance: number;
    negociationRate: number;
    negociationPeriod: number;
    negociationDate: string;
    loanEarlyRedemptionNegociation: boolean;
    negociationType: string;
    loanEarlyRedemptionDate: string;
    thirdPartyCompany: {
        id: string;
        name: string;
    };
    bankAccount: {
        id: string;
        name: string;
    };
    organisationType: {
        value: "BANK" | "THIRDPARTY";
        text: "Banque" | "Fournisseur";
    };
    paymentDetails?: ModifiableLoan[]; // Payment details introduced in the frontend by hand
    tags: { id: string; name: string }[];
};

/**
 * TODO. There is a copy of this type in the frontendm in the types folder. It should be moved to a shared location.
 */
export type LoanDetails = Pick<
    LoanDetailsDTO,
    | "title"
    | "startDate"
    | "bobbeeStartDate"
    | "amortisationType"
    | "loanPeriod"
    | "netAmount"
    | "loanRate"
    | "insuranceRate"
    | "insuranceAmount"
    | "monthlyLoanRate"
    | "aaMonthlyName"
    | "aaInterestName"
    | "aaIraName"
    | "loanEarlyRedemption"
    | "loanEarlyRedemptionNegociation"
    | "negociationType"
    | "negociationRate"
    | "negociationDate"
    | "negociationPeriod"
    | "loanEarlyRedemptionDate"
    | "redemptionType"
    | "iraRedemptionAmount"
    | "earlyRedemptionAmount"
    | "earlyRedemptionInsurance"
    | "earlyRedemptionInterest"
    | "paymentDetails"
    | "periodicity"
>;
/**
 * TODO. There is a copy of this type in the frontend. It should be moved to a shared location.
 */
export type PaymentDetails = {
    month: string;
    monthlyPayment: number;
    monthlyInterest: number;
    amortisation: number;
    monthlyInsurance: number;
    dueAmountStartPeriod: number;
    dueAmountEndPeriod: number;
    iraRedemptionAmount?: number;
    partialRedemptionAmount?: number;
    partialRedemptionInterest?: number;
    partialRedemptionInsurance?: number;
};
/**
 * TODO. There is a copy of this type in the frontend. It should be moved to a shared location.
 */
export type WithSelection<T> = T & { selected?: boolean };

/**
 * TODO. There is a copy of this type in the frontend. It should be moved to a shared location.
 */
export type LoanModifiable<T> = T & {
    modifiedFields?: Array<keyof PaymentDetails>;
    formattedValues?: Partial<Record<keyof PaymentDetails, string>>;
};

/**
 * TODO. There is a copy of this type in the frontend. It should be moved to a shared location.
 */
export type ModifiableLoan = LoanModifiable<WithSelection<PaymentDetails>>;

/**
 * Arrondit un nombre à 2 décimales
 * @param {number} value - Valeur à arrondir
 * @returns {number} - Valeur arrondie
 */
// const roundToTwo = (value: number): number => parseFloat(value.toFixed(2));

/**
 * Calculates monthly payment details for a given period.
 * @param {LoanDetails} loan - The loan details.
 * @param {number} dueAmountEndPeriod - The due amount at the end of the period.
 * @param {number} month - The current month.
 * @returns {PaymentDetails} - The details of the monthly payment.
 */
export type CalculateMonthlyDetailsLoanDetails = Pick<
    LoanDetails,
    | "monthlyLoanRate"
    | "insuranceRate"
    | "insuranceAmount"
    | "amortisationType"
    | "netAmount"
    | "loanPeriod"
    | "startDate"
    | "paymentDetails"
    | "periodicity"
>;
export const calculateMonthlyDetails = (
    loan: CalculateMonthlyDetailsLoanDetails,
    dueAmountEndPeriod: number,
    month: number
): ModifiableLoan => {
    const monthlyLoanRate = loan.monthlyLoanRate * (loan.periodicity || 1);
    const monthlyInterest = (dueAmountEndPeriod * monthlyLoanRate) / 100;
    let monthlyInsurance = loan.insuranceRate
        ? (dueAmountEndPeriod * loan.insuranceRate * (loan.periodicity || 1)) / 12 / 100
        : loan.insuranceAmount;

    if (!monthlyInsurance) {
        monthlyInsurance = 0;
    }

    let amortisation: number;
    let monthlyPayment: number;

    if (loan.amortisationType === "CONSTANT") {
        // Calculate the annuity factor for constant amortisation type
        const annuityFactor =
            Math.pow(1 + monthlyLoanRate / 100, loan.loanPeriod) /
            (Math.pow(1 + monthlyLoanRate / 100, loan.loanPeriod) - 1);
        monthlyPayment = ((loan.netAmount * monthlyLoanRate) / 100) * annuityFactor;
        amortisation = monthlyPayment - monthlyInterest - monthlyInsurance;
    } else if (loan.amortisationType === "DEGRESSIVE") {
        // Calculate amortisation for degressive type
        amortisation = loan.netAmount / loan.loanPeriod;
        monthlyPayment = amortisation + monthlyInterest + monthlyInsurance;
    }
    const periodicity = loan.periodicity || 1;
    const monthString = calculatePaymentMonth(loan.startDate, month, periodicity);

    return {
        month: monthString,
        monthlyPayment: monthlyPayment,
        monthlyInterest: monthlyInterest,
        amortisation: amortisation,
        monthlyInsurance: monthlyInsurance,
        dueAmountStartPeriod: dueAmountEndPeriod,
        dueAmountEndPeriod: dueAmountEndPeriod - amortisation,
    };
};

/**
 * Handles full loan redemption.
 * @param {PaymentDetails[]} payments - The list of payments.
 * @param {LoanDetails} loan - The loan details.
 * @param {number} redemptionMonthIndex - The index of the month of redemption.
 * @returns {PaymentDetails[]} - The updated list of payments.
 */
type handleFullRedemptionLoanDetails = Pick<LoanDetails, "iraRedemptionAmount">;
export const handleFullRedemption = (
    payments: PaymentDetails[],
    loan: handleFullRedemptionLoanDetails,
    redemptionMonthIndex: number
): PaymentDetails[] => {
    const remainingCapital = payments[redemptionMonthIndex].dueAmountStartPeriod;
    const iraRedemptionAmount = loan.iraRedemptionAmount || 0;

    // Adjust the payment at the redemption date
    payments[redemptionMonthIndex].monthlyPayment = remainingCapital + iraRedemptionAmount;
    payments[redemptionMonthIndex].amortisation = remainingCapital;
    payments[redemptionMonthIndex].monthlyInterest = 0;
    payments[redemptionMonthIndex].monthlyInsurance = 0;
    payments[redemptionMonthIndex].dueAmountEndPeriod = 0;
    payments[redemptionMonthIndex].iraRedemptionAmount = iraRedemptionAmount;

    // Return the payments up to the redemption month
    return payments.slice(0, redemptionMonthIndex + 1);
};

/**
 * Handles partial loan redemption.
 * @param {PaymentDetails[]} payments - The list of payments.
 * @param {LoanDetails} loan - The loan details.
 * @param {number} redemptionMonthIndex - The index of the month of redemption.
 * @param {number} totalLoanMonths - The total number of loan months.
 * @returns {PaymentDetails[]} - The updated list of payments.
 */
export const handlePartialRedemption = (
    payments: PaymentDetails[],
    loan: LoanDetails,
    redemptionMonthIndex: number,
    totalLoanMonths: number
): PaymentDetails[] => {
    const partialRedemptionAmount = loan.earlyRedemptionAmount;
    const partialRedemptionInterest = loan.earlyRedemptionInterest;
    const partialRedemptionInsurance = loan.earlyRedemptionInsurance;
    const iraRedemptionAmount = loan.iraRedemptionAmount || 0;
    // Adjust the payment at the redemption date
    payments[redemptionMonthIndex].monthlyPayment =
        partialRedemptionAmount +
        partialRedemptionInterest +
        partialRedemptionInsurance +
        iraRedemptionAmount;
    payments[redemptionMonthIndex].amortisation = partialRedemptionAmount;
    payments[redemptionMonthIndex].dueAmountEndPeriod =
        payments[redemptionMonthIndex].dueAmountStartPeriod - partialRedemptionAmount;
    payments[redemptionMonthIndex].monthlyInterest = partialRedemptionInterest;
    payments[redemptionMonthIndex].monthlyInsurance = partialRedemptionInsurance;
    payments[redemptionMonthIndex].iraRedemptionAmount = iraRedemptionAmount;

    let dueAmountEndPeriod = payments[redemptionMonthIndex].dueAmountEndPeriod;

    // Remove subsequent payments after redemption
    payments.splice(redemptionMonthIndex + 1, totalLoanMonths - redemptionMonthIndex);

    // Recalculate payments for the remaining months
    for (let month = redemptionMonthIndex + 1; month < totalLoanMonths; month++) {
        if (dueAmountEndPeriod <= 0) break; // Stop if the remaining capital is zero or negative

        const monthlyDetails = calculateMonthlyDetails(loan, dueAmountEndPeriod, month);

        // Ensure that amortisation does not exceed the remaining capital
        if (monthlyDetails.amortisation > dueAmountEndPeriod) {
            monthlyDetails.amortisation = dueAmountEndPeriod;
            monthlyDetails.monthlyPayment =
                monthlyDetails.amortisation +
                monthlyDetails.monthlyInterest +
                monthlyDetails.monthlyInsurance;
            monthlyDetails.dueAmountEndPeriod = 0;
        }

        dueAmountEndPeriod = monthlyDetails.dueAmountEndPeriod;
        payments.push(monthlyDetails);
    }

    return payments;
};

/**
 * Handles the renegotiation of loan rate.
 * @param {PaymentDetails[]} payments - The list of payments.
 * @param {LoanDetails} loan - The loan details.
 * @param {number} negociationMonthIndex - The index of the month of renegotiation.
 * @param {number} totalLoanMonths - The total number of loan months.
 * @returns {PaymentDetails[]} - The updated list of payments.
 */
type HandleNegociationRateLoanDetails = Pick<
    LoanDetails,
    | "negociationRate"
    | "loanRate"
    | "monthlyLoanRate"
    | "insuranceAmount"
    | "insuranceRate"
    | "startDate"
    | "loanPeriod"
    | "amortisationType"
    | "netAmount"
>;
const handleNegociationRate = (
    payments: PaymentDetails[],
    loan: HandleNegociationRateLoanDetails,
    negociationMonthIndex: number,
    totalLoanMonths: number
): PaymentDetails[] => {
    const newLoanRate = loan.negociationRate;
    if (newLoanRate === loan.loanRate) {
        // If the new rate is the same as the original rate, return the original payments
        return payments;
    }
    loan.monthlyLoanRate = newLoanRate / 12; // Assuming annual rate is provided

    let dueAmountEndPeriod = payments[negociationMonthIndex].dueAmountEndPeriod;

    // Remove subsequent payments after renegotiation
    payments.splice(negociationMonthIndex, totalLoanMonths - negociationMonthIndex);

    // Recalculate payments for the remaining months with new rate
    for (let month = negociationMonthIndex; month <= totalLoanMonths; month++) {
        if (dueAmountEndPeriod <= 0) break; // Stop if the remaining capital is zero or negative

        const monthlyDetails = calculateMonthlyDetails(loan, dueAmountEndPeriod, month);

        // Ensure that amortisation does not exceed the remaining capital
        if (monthlyDetails.amortisation > dueAmountEndPeriod) {
            monthlyDetails.amortisation = dueAmountEndPeriod;
            monthlyDetails.monthlyPayment =
                monthlyDetails.amortisation +
                monthlyDetails.monthlyInterest +
                monthlyDetails.monthlyInsurance;
            monthlyDetails.dueAmountEndPeriod = 0;
        }

        dueAmountEndPeriod = monthlyDetails.dueAmountEndPeriod;
        payments.push(monthlyDetails);
    }

    return payments;
};

/**
 * Handles the renegotiation of loan period.
 * @param {PaymentDetails[]} payments - The list of payments.
 * @param {LoanDetails} loan - The loan details.
 * @param {number} negociationMonthIndex - The index of the month of renegotiation.
 * @param {number} newTotalLoanMonths - The new total number of loan months.
 * @returns {PaymentDetails[]} - The updated list of payments.
 */
const handleNegociationPeriod = (
    payments: PaymentDetails[],
    loan: LoanDetails,
    negociationMonthIndex: number,
    newTotalLoanMonths: number
): PaymentDetails[] => {
    if (newTotalLoanMonths === loan.loanPeriod) {
        // If the new period is the same as the original period, return the original payments
        return payments;
    }

    let dueAmountEndPeriod = payments[negociationMonthIndex].dueAmountEndPeriod;
    payments.splice(negociationMonthIndex, payments.length - negociationMonthIndex);

    // Recalculate payments for the remaining months with new period
    for (let month = negociationMonthIndex + 1; month <= newTotalLoanMonths; month++) {
        if (dueAmountEndPeriod <= 0) break; // Stop if the remaining capital is zero or negative

        const monthlyDetails = calculateMonthlyDetails(loan, dueAmountEndPeriod, month);

        // Ensure that amortisation does not exceed the remaining capital
        if (monthlyDetails.amortisation > dueAmountEndPeriod) {
            monthlyDetails.amortisation = dueAmountEndPeriod;
            monthlyDetails.monthlyPayment =
                monthlyDetails.amortisation +
                monthlyDetails.monthlyInterest +
                monthlyDetails.monthlyInsurance;
            monthlyDetails.dueAmountEndPeriod = 0;
        }

        dueAmountEndPeriod = monthlyDetails.dueAmountEndPeriod;
        payments.push(monthlyDetails);
    }

    return payments;
};

/**
 * Calculates the loan payment schedule.
 * @param {LoanDetails} loan - The loan details.
 * @returns {PaymentDetails[]} - The payment schedule.
 */
export type CalculateLoanDetails = Pick<
    LoanDetails,
    | "netAmount"
    | "loanRate"
    | "loanPeriod"
    | "amortisationType"
    | "startDate"
    | "bobbeeStartDate"
    | "loanEarlyRedemptionNegociation"
    | "negociationDate"
    | "negociationType"
    | "negociationRate"
    | "negociationPeriod"
    | "loanEarlyRedemptionDate"
    | "redemptionType"
    | "iraRedemptionAmount"
    | "earlyRedemptionAmount"
    | "earlyRedemptionInsurance"
    | "earlyRedemptionInterest"
    | "loanEarlyRedemption"
    | "monthlyLoanRate"
    | "insuranceRate"
    | "insuranceAmount"
    | "aaMonthlyName"
    | "aaInterestName"
    | "aaIraName"
    | "title"
    | "paymentDetails"
>;

/**
 * Calculates the payment details for a loan.
 * @param loan containing all the loan information, a subset of a Document
 * @returns The payment details for the loan, or undefined if there exists manual loan data but it is invalid
 * @throws Error if the manual loan data is invalid ⚠️
 */
export const calculateLoan = (loan: CalculateLoanDetails): PaymentDetails[] | undefined => {
    if (
        !loan ||
        !loan.netAmount ||
        !loan.loanRate ||
        !loan.loanPeriod ||
        !loan.amortisationType ||
        !loan.startDate ||
        !loan.bobbeeStartDate
    ) {
        console.log("No loan data available");
        return [];
    }

    const totalLoanMonths = loan.loanPeriod;
    const bobbeeStartDate = moment(loan.bobbeeStartDate);
    if (loan.paymentDetails && loan.paymentDetails.length > 0) {
        return calculateManualPayments(loan);
    }
    let payments: PaymentDetails[] = [];
    if (loan.paymentDetails && loan.paymentDetails.length > 0) {
        // The user has manually entered monthlyInterest, monthlyInsurance, amortisation, etc.
        payments = calculateManualPayments(loan); // see next code block
    } else {
        let dueAmountEndPeriod = loan.netAmount;

        for (let month = 1; month <= totalLoanMonths; month++) {
            const monthlyDetails = calculateMonthlyDetails(loan, dueAmountEndPeriod, month);
            monthlyDetails.monthlyPayment = monthlyDetails.monthlyPayment;
            monthlyDetails.monthlyInterest = monthlyDetails.monthlyInterest;
            monthlyDetails.amortisation = monthlyDetails.amortisation;
            monthlyDetails.dueAmountEndPeriod = monthlyDetails.dueAmountEndPeriod;
            monthlyDetails.dueAmountStartPeriod = monthlyDetails.dueAmountStartPeriod;
            if (monthlyDetails.monthlyInsurance) {
                monthlyDetails.monthlyInsurance = monthlyDetails.monthlyInsurance;
            }

            dueAmountEndPeriod = monthlyDetails.dueAmountEndPeriod;
            payments.push(monthlyDetails);
        }
    }

    // Appliquer la logique de renégociation
    if (
        loan.loanEarlyRedemptionNegociation &&
        loan.negociationDate &&
        loan.negociationType &&
        (loan.negociationRate || loan.negociationPeriod)
    ) {
        const negociationDate = moment(loan.negociationDate, "DD/MM/YYYY");
        const negociationMonthIndex = payments.findIndex((payment) =>
            moment(payment.month, "DD/MM/YYYY").isSameOrAfter(negociationDate)
        );

        if (negociationMonthIndex !== -1) {
            if (loan.negociationType === "RATE" && loan.negociationRate) {
                return handleNegociationRate(
                    payments,
                    loan,
                    negociationMonthIndex,
                    totalLoanMonths
                );
            } else if (loan.negociationType === "DURATION" && loan.negociationPeriod) {
                const newTotalLoanMonths = negociationMonthIndex + loan.negociationPeriod;
                return handleNegociationPeriod(
                    payments,
                    loan,
                    negociationMonthIndex,
                    newTotalLoanMonths
                );
            } else {
                console.log("Invalid negociation data");
            }
        }
    }

    // Appliquer la logique de remboursement anticipé
    if (loan.loanEarlyRedemption && loan.loanEarlyRedemptionDate) {
        const redemptionDate = moment(loan.loanEarlyRedemptionDate, "DD/MM/YYYY");
        const redemptionMonthIndex = payments.findIndex((payment) =>
            moment(payment.month, "DD/MM/YYYY").isSameOrAfter(redemptionDate)
        );

        if (redemptionMonthIndex !== -1) {
            if (loan.redemptionType === "FULL") {
                return handleFullRedemption(payments, loan, redemptionMonthIndex);
            } else if (loan.redemptionType === "PARTIAL" && loan.earlyRedemptionAmount) {
                return handlePartialRedemption(
                    payments,
                    loan,
                    redemptionMonthIndex,
                    totalLoanMonths
                );
            } else {
                console.log("Invalid early redemption data");
            }
        }
    }

    // Appliquer la logique de la date de reprise (bobbeeStartDate)
    const bobbeeStartIndex = payments.findIndex((payment) =>
        moment(payment.month, "DD/MM/YYYY").isSame(moment(bobbeeStartDate, "DD/MM/YYYY"))
    );

    // Si la date exacte n'est pas trouvée, prendre le premier paiement après cette date
    if (bobbeeStartIndex === -1) {
        const closestIndex = payments.findIndex((payment) =>
            moment(payment.month, "DD/MM/YYYY").isAfter(moment(bobbeeStartDate))
        );
        if (closestIndex !== -1) {
            payments = payments.slice(closestIndex);
        }
    } else {
        payments = payments.slice(bobbeeStartIndex);
    }

    // loan.loanPeriod = payments.length;

    return payments;
};

/**
 * If the user supplies manual (edited) payment details, this function
 * overrides the normal schedule by using those details directly.
 *
 * - Summation for monthlyPayment = monthlyInterest + monthlyInsurance + amortisation
 * - dueAmountStartPeriod / dueAmountEndPeriod are chained so that each month
 *   starts where the last one ended.
 * @param {LoanDetails} loan - The loan details or undefined if table is not okay (throws error).
 * @returns {PaymentDetails[]} - The payment schedule.
 * @throws {Error} -  ⚠️ If the manual amortisation exceeds the remaining capital.
 */
export function calculateManualPayments(loan: CalculateLoanDetails): PaymentDetails[] | undefined {
    const manualEntries = loan.paymentDetails as ModifiableLoan[] | undefined;

    // If no manual entries, return an empty array
    if (!manualEntries || manualEntries.length === 0) {
        return [];
    }

    let currentDueAmount = loan.netAmount;
    const results: PaymentDetails[] = [];

    for (let i = 0; i < manualEntries.length; i++) {
        const entry = manualEntries[i];

        const monthlyInterest = entry.monthlyInterest || 0;
        const monthlyInsurance = entry.monthlyInsurance || 0;
        let amortisation = entry.amortisation || 0;

        if (amortisation > currentDueAmount) {
            amortisation = currentDueAmount; // Clamp amortisation to the remaining capital
        }

        // Otherwise, proceed normally
        const monthlyPayment = monthlyInterest + monthlyInsurance + amortisation;

        const dueAmountStartPeriod = currentDueAmount;
        const newDueAmount = dueAmountStartPeriod - amortisation;

        const manualPayment: PaymentDetails = {
            ...entry,
            monthlyPayment,
            monthlyInterest,
            monthlyInsurance,
            amortisation,
            dueAmountStartPeriod,
            dueAmountEndPeriod: newDueAmount,
        };

        results.push(manualPayment);
        currentDueAmount = newDueAmount;
    }

    return results;
}

/**
 * Recalculates all manual payments in-place:
 *  - If an early row’s amortisation is more than the leftover, clamp it to leftover.
 *  - For the final row, force the leftover to be completely paid.
 *  - monthlyPayment = monthlyInterest + monthlyInsurance + final amortisation
 *
 * This way, “differences” get pushed to future rows, and the final row
 * ensures leftover is 0.
 */
export function recalcAllManualPayments(
    payments: ModifiableLoan[],
    loan: LoanDetails,
    startIndex: number = 0
) {
    if (!payments.length) return;

    // 1) Get leftover from the row just before startIndex, or netAmount if none
    let leftover = startIndex > 0 ? payments[startIndex - 1].dueAmountEndPeriod : loan.netAmount;

    // 2) Process all payments starting from startIndex
    for (let i = startIndex; i < payments.length; i++) {
        const entry = payments[i];

        // ✅ If remaining balance is 0 or negative, stop amortization & reset future payments
        if (leftover <= 0) {
            entry.monthlyPayment = 0;
            entry.monthlyInterest = 0;
            entry.amortisation = 0;
            entry.monthlyInsurance = 0;
            entry.dueAmountStartPeriod = 0;
            entry.dueAmountEndPeriod = 0;
            entry.formattedValues = {
                amortisation: newCommonFormatCurrency(0),
                monthlyInterest: newCommonFormatCurrency(0),
                monthlyInsurance: newCommonFormatCurrency(0),
                monthlyPayment: newCommonFormatCurrency(0),
                dueAmountEndPeriod: newCommonFormatCurrency(0),
            };
            continue; // Move to next months, setting everything to 0
        }

        // 3) Manual row recalculation for the first modified row
        if (i === startIndex) {
            const monthlyInterest =
                (entry.dueAmountStartPeriod * (loan.monthlyLoanRate * (loan.periodicity || 1))) /
                100;

            const monthlyInsurance = ((entry.monthlyInsurance || 0) / 12) * (loan.periodicity || 1);
            let desiredAmortisation = entry.amortisation || 0;

            // Ensure amortization does not exceed leftover
            if (desiredAmortisation > leftover) {
                desiredAmortisation = leftover;
            }

            entry.dueAmountStartPeriod = leftover;
            entry.amortisation = desiredAmortisation;

            leftover = leftover - desiredAmortisation;
            if (leftover < 0) leftover = 0;

            entry.monthlyInterest = monthlyInterest;
            entry.monthlyInsurance = monthlyInsurance;
            entry.monthlyPayment = monthlyInterest + monthlyInsurance + desiredAmortisation;
            entry.dueAmountEndPeriod = leftover;
        } else {
            // 4) Auto-calculate for future months
            const iterationIndex = i - startIndex + 1; // Fix for correct month calculation
            const autoDetails = calculateMonthlyDetails(loan, leftover, iterationIndex);

            payments[i].month = autoDetails.month;
            payments[i].monthlyPayment = autoDetails.monthlyPayment;
            payments[i].monthlyInterest = autoDetails.monthlyInterest;
            payments[i].amortisation = autoDetails.amortisation;
            payments[i].monthlyInsurance = autoDetails.monthlyInsurance;
            payments[i].dueAmountStartPeriod = autoDetails.dueAmountStartPeriod;
            payments[i].dueAmountEndPeriod = autoDetails.dueAmountEndPeriod;

            leftover = payments[i].dueAmountEndPeriod;
            if (leftover < 0) leftover = 0;
        }

        // 5) Format values for UI
        payments[i].formattedValues = {
            amortisation: newCommonFormatCurrency(payments[i].amortisation),
            monthlyInterest: newCommonFormatCurrency(payments[i].monthlyInterest),
            monthlyInsurance: newCommonFormatCurrency(payments[i].monthlyInsurance),
            monthlyPayment: newCommonFormatCurrency(payments[i].monthlyPayment),
            dueAmountEndPeriod: newCommonFormatCurrency(payments[i].dueAmountEndPeriod),
        };
    }

    // ✅ 6) Ensure the last row clears all remaining capital
    const lastIndex = payments.length - 1;
    if (lastIndex >= 0 && leftover > 0) {
        payments[lastIndex].amortisation += leftover; // Add leftover to final amortisation
        payments[lastIndex].monthlyPayment += leftover; // Adjust the final payment
        payments[lastIndex].dueAmountEndPeriod = 0; // Debt should be fully paid off

        // Update formatting
        payments[lastIndex].formattedValues["amortisation"] = newCommonFormatCurrency(
            payments[lastIndex].amortisation
        );
        payments[lastIndex].formattedValues["monthlyPayment"] = newCommonFormatCurrency(
            payments[lastIndex].monthlyPayment
        );
        payments[lastIndex].formattedValues["dueAmountEndPeriod"] = newCommonFormatCurrency(0);
    }
}

/**
 * Calculates the month where the i-th payment should be made.
 * @param {string} startDate - The start date of the loan.
 * @param {number} iterationIndex - The index of the payment.
 * @param {number} periodicity - The periodicity of the payment.
 */
export function calculatePaymentMonth(
    startDate: string,
    iterationIndex: number,
    periodicity: number // e.g. 1, 2, 3, 6, or 12
): string {
    return moment(startDate, "YYYY-MM-DD") // or "DD/MM/YYYY" depending on your usage
        .add((iterationIndex - 1) * periodicity, "months")
        .format("DD/MM/YYYY");
}
